"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Building2, Save } from "lucide-react";

export interface SettingsPageProps {
  title: string;
  description?: string;
  sections?: { title: string; fields: SettingField[] }[];
}

interface SettingField {
  key: string;
  label: string;
  type: "text" | "textarea" | "number" | "select" | "switch" | "date";
  defaultValue?: string | number | boolean;
  options?: { value: string; label: string }[];
  help?: string;
}

const DEFAULT_SECTIONS: { title: string; fields: SettingField[] }[] = [
  {
    title: "Organization Profile",
    fields: [
      { key: "orgName", label: "Company Name", type: "text", defaultValue: "OMEGA Distribution Ltd." },
      { key: "legalName", label: "Legal Entity Name", type: "text", defaultValue: "OMEGA Distribution Bangladesh Ltd." },
      { key: "bin", label: "BIN / VAT Number", type: "text", defaultValue: "000-XXXX-1234" },
      { key: "taxId", label: "TIN", type: "text", defaultValue: "123456789012" },
      { key: "address", label: "Registered Address", type: "textarea", defaultValue: "Level 7, Gulshan Avenue, Dhaka 1212" },
      { key: "phone", label: "Phone", type: "text", defaultValue: "+880 2 9876543" },
      { key: "email", label: "Email", type: "text", defaultValue: "info@omega-dist.bd" },
      { key: "currency", label: "Base Currency", type: "select", options: [{ value: "BDT", label: "BDT (৳)" }, { value: "USD", label: "USD ($)" }], defaultValue: "BDT" },
    ],
  },
  {
    title: "Fiscal Year",
    fields: [
      { key: "fyStart", label: "Fiscal Year Start", type: "date", defaultValue: "2026-01-01" },
      { key: "fyEnd", label: "Fiscal Year End", type: "date", defaultValue: "2026-12-31" },
      { key: "fyLabel", label: "FY Label", type: "text", defaultValue: "FY 2026" },
    ],
  },
  {
    title: "Localization",
    fields: [
      { key: "lang", label: "Default Language", type: "select", options: [{ value: "en", label: "English" }, { value: "bn", label: "বাংলা" }], defaultValue: "en" },
      { key: "timezone", label: "Timezone", type: "select", options: [{ value: "Asia/Dhaka", label: "Asia/Dhaka (GMT+6)" }], defaultValue: "Asia/Dhaka" },
      { key: "dateFormat", label: "Date Format", type: "select", options: [{ value: "DD/MM/YYYY", label: "DD/MM/YYYY" }, { value: "YYYY-MM-DD", label: "YYYY-MM-DD" }], defaultValue: "YYYY-MM-DD" },
    ],
  },
];

export function SettingsPage({ title, description, sections = DEFAULT_SECTIONS }: SettingsPageProps) {
  const { toast } = useToast();
  const [values, setValues] = React.useState<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    for (const s of sections) for (const f of s.fields) v[f.key] = f.defaultValue ?? (f.type === "switch" ? false : f.type === "number" ? 0 : "");
    return v;
  });

  const set = (k: string, val: unknown) => setValues(s => ({ ...s, [k]: val }));

  const onSave = () => {
    try { localStorage.setItem("omega-settings", JSON.stringify(values)); } catch { /* ignore */ }
    toast({ title: "Settings saved", description: "Organization settings have been persisted." });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="text-primary size-5" />
            <h1 className="text-xl font-semibold sm:text-2xl">{title}</h1>
          </div>
          {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
        </div>
        <Button onClick={onSave}><Save className="size-4" /> Save Settings</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {sections.map((sec, si) => (
          <Card key={si}>
            <CardHeader>
              <CardTitle className="text-base">{sec.title}</CardTitle>
              <CardDescription className="text-xs">Configure {sec.title.toLowerCase()}.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {sec.fields.map(f => (
                <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2 flex flex-col gap-1.5" : "flex flex-col gap-1.5"}>
                  <Label htmlFor={f.key}>{f.label}</Label>
                  {f.type === "text" || f.type === "date" ? (
                    <Input id={f.key} type={f.type === "date" ? "date" : "text"} value={String(values[f.key] ?? "")} onChange={e => set(f.key, e.target.value)} />
                  ) : f.type === "number" ? (
                    <Input id={f.key} type="number" value={Number(values[f.key] ?? 0)} onChange={e => set(f.key, Number(e.target.value))} />
                  ) : f.type === "textarea" ? (
                    <Textarea id={f.key} value={String(values[f.key] ?? "")} onChange={e => set(f.key, e.target.value)} />
                  ) : f.type === "select" && f.options ? (
                    <Select value={String(values[f.key] ?? "")} onValueChange={v => set(f.key, v)}>
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>{f.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "switch" ? (
                    <div className="flex items-center gap-2 pt-1">
                      <Switch checked={Boolean(values[f.key])} onCheckedChange={v => set(f.key, v)} />
                      <span className="text-muted-foreground text-xs">{values[f.key] ? "Enabled" : "Disabled"}</span>
                    </div>
                  ) : (
                    <Input id={f.key} value={String(values[f.key] ?? "")} onChange={e => set(f.key, e.target.value)} />
                  )}
                  {f.help && <p className="text-muted-foreground text-xs">{f.help}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
